import {
    getLayoutDefaults,
    NUMBER_FORMAT_DEFAULTS,
} from "@common/constants/reports/report-settings-defaults";
import { LayoutValues } from "@common/schemas/domains/reportSettings/layout-setting.schema";
import { NumberFormatValues } from "@common/schemas/domains/reportSettings/number-format-setting.schema";
import { ReportSettingsConcreteReportKey } from "@common/types/reports/report-settings-report-key";
import {
    ReportSettingCandidate,
    ReportSettingsResolutionInput,
    buildReportSettingCandidates,
} from "./report-setting-candidates";
import { ReportSettingKey, ReportSettingValues } from "./report-setting-fields";
import { ReportSettingSource } from "./report-setting-source";

export interface ResolvedReportSetting<K extends ReportSettingKey> {
    value: ReportSettingValues[K];
    source: ReportSettingSource;
}

export type ResolvedReportSettings = {
    [K in ReportSettingKey]: ResolvedReportSetting<K>;
};

export interface ReportSettingsValuesResolution {
    layout: LayoutValues;
    numberFormat: NumberFormatValues;
}

export interface ReportSettingsResolution extends ReportSettingsValuesResolution {
    settings: ResolvedReportSettings;
}

export const getReportSettingDefaults = (
    reportKey: ReportSettingsConcreteReportKey,
): ReportSettingValues => ({
    ...getLayoutDefaults(reportKey),
    ...NUMBER_FORMAT_DEFAULTS,
});

export const findStoredReportSetting = <K extends ReportSettingKey>(
    key: K,
    candidates: ReportSettingCandidate[],
): ResolvedReportSetting<K> | null => {
    for (const { values, source } of candidates) {
        const value = values[key];
        if (value === null) {
            continue;
        }
        return { value, source };
    }
    return null;
};

export const splitReportSettingValues = (
    values: ReportSettingValues,
): ReportSettingsValuesResolution => ({
    layout: {
        header: values.header,
        title: values.title,
        subtitle: values.subtitle,
        subtitle2: values.subtitle2,
        footer: values.footer,
        memo: values.memo,
        watermarkText: values.watermarkText,
        logoOption: values.logoOption,
        rowVisibility: values.rowVisibility,
    },
    numberFormat: {
        decimalPlaces: values.decimalPlaces,
        negativeFormat: values.negativeFormat,
        isNegativeInRed: values.isNegativeInRed,
        hasThousandsSeparator: values.hasThousandsSeparator,
        zeroDisplay: values.zeroDisplay,
        currencySymbol: values.currencySymbol,
        currencyPlacement: values.currencyPlacement,
        percentDecimalPlaces: values.percentDecimalPlaces,
        amountScale: values.amountScale,
    },
});

interface ResolveFieldInput {
    candidates: ReportSettingCandidate[];
    defaults: ReportSettingValues;
    defaultSource: ReportSettingSource;
}

const resolveField = <K extends ReportSettingKey>(
    key: K,
    { candidates, defaults, defaultSource }: ResolveFieldInput,
): ResolvedReportSetting<K> =>
    findStoredReportSetting(key, candidates) ?? {
        value: defaults[key],
        source: defaultSource,
    };

export const resolveReportSettings = (
    input: ReportSettingsResolutionInput,
): ReportSettingsResolution => {
    const field: ResolveFieldInput = {
        candidates: buildReportSettingCandidates(input),
        defaults: getReportSettingDefaults(input.reportKey),
        defaultSource: { kind: "kick-default", reportKey: input.reportKey },
    };
    const settings: ResolvedReportSettings = {
        header: resolveField("header", field),
        title: resolveField("title", field),
        subtitle: resolveField("subtitle", field),
        subtitle2: resolveField("subtitle2", field),
        footer: resolveField("footer", field),
        memo: resolveField("memo", field),
        watermarkText: resolveField("watermarkText", field),
        logoOption: resolveField("logoOption", field),
        rowVisibility: resolveField("rowVisibility", field),
        decimalPlaces: resolveField("decimalPlaces", field),
        negativeFormat: resolveField("negativeFormat", field),
        isNegativeInRed: resolveField("isNegativeInRed", field),
        hasThousandsSeparator: resolveField("hasThousandsSeparator", field),
        zeroDisplay: resolveField("zeroDisplay", field),
        currencySymbol: resolveField("currencySymbol", field),
        currencyPlacement: resolveField("currencyPlacement", field),
        percentDecimalPlaces: resolveField("percentDecimalPlaces", field),
        amountScale: resolveField("amountScale", field),
    };
    return {
        settings,
        ...splitReportSettingValues({
            header: settings.header.value,
            title: settings.title.value,
            subtitle: settings.subtitle.value,
            subtitle2: settings.subtitle2.value,
            footer: settings.footer.value,
            memo: settings.memo.value,
            watermarkText: settings.watermarkText.value,
            logoOption: settings.logoOption.value,
            rowVisibility: settings.rowVisibility.value,
            decimalPlaces: settings.decimalPlaces.value,
            negativeFormat: settings.negativeFormat.value,
            isNegativeInRed: settings.isNegativeInRed.value,
            hasThousandsSeparator: settings.hasThousandsSeparator.value,
            zeroDisplay: settings.zeroDisplay.value,
            currencySymbol: settings.currencySymbol.value,
            currencyPlacement: settings.currencyPlacement.value,
            percentDecimalPlaces: settings.percentDecimalPlaces.value,
            amountScale: settings.amountScale.value,
        }),
    };
};
